import React, { useEffect, useState, useRef } from "react";
import { aiAPI } from "../api/client";
import { toast } from "react-toastify";
import "./AIAssistant.css";
import LoadingButton from "../components/LoadingButton";

const FORMAT_FIELD_ORDER = [
  "name", "role", "total_experience", "experience_summary", "technical_skills",
  "work_experience", "key_projects_summary", "projects", "education", "additional_information",
];
const FORMAT_MULTILINE_FIELDS = new Set(FORMAT_FIELD_ORDER.slice(3));

export default function AIAssistant() {
  const [activeTab, setActiveTab] = useState("analyze");
  const [loading, setLoading] = useState(false);

  /* ================= Resume vs JD ================= */
  const [selectedAiModel, setSelectedAiModel] = useState("gemini-2.5-flash");
  const [activeAiModels, setActiveAiModels] = useState([]);
  const [resumeUploadLimit, setResumeUploadLimit] = useState(1);
  const [resumeUploads, setResumeUploads] = useState([]);
  const [parsedResumes, setParsedResumes] = useState([]);
  const [jobDescription, setJobDescription] = useState("");
  const [primarySkills, setPrimarySkills] = useState([]);
  const [newPrimarySkill, setNewPrimarySkill] = useState("");
  const [showPrimarySkillInput, setShowPrimarySkillInput] = useState(false);
  const [analysisResults, setAnalysisResults] = useState([]);
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);

  /* ================= JD Suggestions ================= */
  const [suggestions, setSuggestions] = useState(null);

  /* ================= Format Resume ================= */
  const [formatAiModel, setFormatAiModel] = useState("gemini-2.5-flash");
  const [formatResumeUploadLimit, setFormatResumeUploadLimit] = useState(1);
  const [formatUploads, setFormatUploads] = useState([]);
  const [formatModal, setFormatModal] = useState(null);
  const [formatEditFields, setFormatEditFields] = useState(null);
  const [selectedFormatUploadId] = useState(null);
  const [resumeFields] = useState(null);
  const [downloadType, setDownloadType] = useState("pdf");

  const resumeInputRef = useRef(null);
  const formatInputRef = useRef(null);
  const resumeUploadIdRef = useRef(0);

  /* ================= FILE UPLOAD ================= */

  const parseResumeFile = async (file, uploadId) => {
    try {
      const formData = new FormData();
      formData.append("resume", file);

      const res = await aiAPI.parseResume(formData);
      const successfullyParsedResumes = res.data.resumes || [res.data];

      const failedCount = res.data.failedFiles?.length || 0;
      if (failedCount) {
        setResumeUploads((uploads) =>
          uploads.map((upload) => upload.id === uploadId ? { ...upload, status: "failed" } : upload)
        );
        toast.error(`${file.name} could not be parsed.`);
      } else {
        setParsedResumes((resumes) => [...resumes, ...successfullyParsedResumes]);
        setResumeUploads((uploads) =>
          uploads.map((upload) => upload.id === uploadId ? { ...upload, status: "complete" } : upload)
        );
        toast.success(`${file.name} parsed successfully.`);
      }
    } catch {
      setResumeUploads((uploads) =>
        uploads.map((upload) => upload.id === uploadId ? { ...upload, status: "failed" } : upload)
      );
      toast.error("Resume parsing failed");
    }
  };

  const handleResumeFile = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const remainingSlots = resumeUploadLimit - resumeUploads.length;
    if (files.length > remainingSlots) {
      toast.error(`You can upload ${remainingSlots} more resume${remainingSlots === 1 ? "" : "s"}.`);
      e.target.value = "";
      return;
    }

    const uploads = files.map((file) => ({
      id: ++resumeUploadIdRef.current,
      fileName: file.name,
      status: "parsing",
    }));

    setResumeUploads((currentUploads) => [...currentUploads, ...uploads]);
    setAnalysisResults([]);
    setSelectedAnalysis(null);
    e.target.value = "";

    // Each file is sent immediately, so later uploads never hold up parsing.
    uploads.forEach((upload, index) => parseResumeFile(files[index], upload.id));
  };

  const handleResumeUploadLimitChange = (e) => {
    const nextLimit = Number(e.target.value);
    setResumeUploadLimit(nextLimit);
    setResumeUploads([]);
    setParsedResumes([]);
    setAnalysisResults([]);
    setSelectedAnalysis(null);
  };

  const parsing = resumeUploads.some((upload) => upload.status === "parsing");
  const remainingResumeSlots = resumeUploadLimit - resumeUploads.length;

  const handleSavePrimarySkills = () => {
    const skillsToAdd = newPrimarySkill
      .split(",")
      .map((skill) => skill.trim())
      .filter(Boolean);

    if (!skillsToAdd.length) {
      toast.error("Enter at least one primary skill.");
      return;
    }

    setPrimarySkills((currentSkills) => [
      ...currentSkills,
      ...skillsToAdd.filter((skill) => !currentSkills.includes(skill)),
    ]);
    setNewPrimarySkill("");
    setShowPrimarySkillInput(false);
  };

  const handleRemovePrimarySkill = (skillToRemove) => {
    setPrimarySkills((currentSkills) =>
      currentSkills.filter((skill) => skill !== skillToRemove)
    );
  };

  const handleFormatFile = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const remainingSlots = formatResumeUploadLimit - formatUploads.length;
    if (files.length > remainingSlots) {
      toast.error(`You can upload ${remainingSlots} more resume${remainingSlots === 1 ? "" : "s"}.`);
      e.target.value = "";
      return;
    }

    setFormatUploads((uploads) => [
      ...uploads,
      ...files.map((file) => ({
        id: ++resumeUploadIdRef.current,
        file,
        fileName: file.name,
        status: "ready",
        downloadType: "pdf",
      })),
    ]);
    setFormatModal(null);
    setFormatEditFields(null);
    // Allow selecting the same file again after replacing or editing it.
    e.target.value = "";
  };

  const handleFormatUploadLimitChange = (e) => {
    setFormatResumeUploadLimit(Number(e.target.value));
    setFormatUploads([]);
    setFormatModal(null);
    setFormatEditFields(null);
  };

  /* ================= ANALYZE RESUME ================= */

  const handleAnalyzeResume = async () => {
    if (!parsedResumes.length || !jobDescription) {
      return toast.error("Upload a resume and add a job description");
    }

    setLoading(true);
    try {
      const res = await aiAPI.analyzeResume({
        jobDescription,
        primarySkills,
        model: selectedAiModel,
        resumes: parsedResumes.map((resume) => ({
          fileName: resume.fileName,
          first_name: resume.first_name,
          last_name: resume.last_name,
          experience: resume.experience,
          rawText: resume.rawText,
        })),
      });

      const sortedResults = [...(res.data.analyses || [])].sort(
        (a, b) => getMatchPercent(b.analysis?.matchScore) - getMatchPercent(a.analysis?.matchScore)
      );
      setAnalysisResults(sortedResults);
      setSelectedAnalysis(null);
      toast.success("Resume analyzed successfully");
    } catch (error) {
      const serverMessage = error.response?.data?.error;
      if (serverMessage === "Server in use") {
        setActiveAiModels((models) => [...new Set([...models, selectedAiModel])]);
      } else {
        toast.error(serverMessage || "Analysis failed");
      }
    } finally {
      setLoading(false);
    }
  };

  /* ================= JD SUGGESTIONS ================= */

  const handleGetSuggestions = async () => {
    if (!jobDescription) return toast.error("Enter Job Description");

    setLoading(true);
    try {
      const res = await aiAPI.getJDSuggestions({ jobDescription });
      setSuggestions(res.data.suggestions);
    } catch {
      toast.error("Failed to generate JD suggestions");
    } finally {
      setLoading(false);
    }
  };

  /* ================= FORMAT RESUME FLOW ================= */

  const handleConvertResume = async () => {
    const pendingUploads = formatUploads.filter((upload) => upload.status === "ready");
    if (!pendingUploads.length) return toast.error("Upload a resume to extract");

    setLoading(true);
    setFormatUploads((uploads) => uploads.map((upload) =>
      upload.status === "ready" ? { ...upload, status: "extracting" } : upload
    ));
    try {
      const formData = new FormData();
      pendingUploads.forEach((upload) => formData.append("resume", upload.file));
      formData.append("model", formatAiModel);

      const res = await aiAPI.convertResumeFormat(formData);
      const extractedResumes = res.data.resumes || (res.data.fields ? [{ fields: res.data.fields }] : []);
      const failedFiles = new Set(res.data.failedFiles || []);
      const fieldsByFileName = new Map(extractedResumes.map((resume) => [resume.fileName, resume.fields]));
      const firstUpload = pendingUploads.find((upload) => fieldsByFileName.has(upload.fileName));

      setFormatUploads((uploads) => uploads.map((upload) => {
        const fields = fieldsByFileName.get(upload.fileName);
        if (fields) {
          return { ...upload, status: "complete", fields };
        }
        if (failedFiles.has(upload.fileName)) return { ...upload, status: "failed" };
        return upload.status === "extracting" ? { ...upload, status: "failed" } : upload;
      }));

      if (firstUpload) {
        setFormatModal(null);
        setFormatEditFields(null);
        toast.success(`${extractedResumes.length} resume${extractedResumes.length === 1 ? "" : "s"} extracted. Select View or edit to a resume.`);
      } else {
        toast.error("No resumes could be extracted");
      }
    } catch (error) {
      setFormatUploads((uploads) => uploads.map((upload) =>
        upload.status === "extracting" ? { ...upload, status: "failed" } : upload
      ));
      toast.error(error.response?.data?.error || "Extraction failed");
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (key, value) => {
    setFormatEditFields((fields) => ({ ...fields, [key]: value }));
  };

  const handleOpenFormatModal = (upload, mode) => {
    if (!upload.fields) return;
    setFormatModal({ uploadId: upload.id, mode });
    setFormatEditFields(mode === "edit" ? { ...upload.fields } : null);
  };

  const handleSaveFormatFields = () => {
    if (!formatModal || !formatEditFields) return;
    setFormatUploads((uploads) => uploads.map((upload) =>
      upload.id === formatModal.uploadId
        ? { ...upload, fields: formatEditFields }
        : upload
    ));
    setFormatModal(null);
    setFormatEditFields(null);
    toast.success("Resume changes saved.");
  };

  const handleDownloadTypeChange = (uploadId, type) => {
    setFormatUploads((uploads) => uploads.map((upload) =>
      upload.id === uploadId ? { ...upload, downloadType: type } : upload
    ));
  };

  const getMatchPercent = (score) => {
    const normalizedScore = score ?? 0;
    const percent = normalizedScore <= 1 ? normalizedScore * 100 : normalizedScore;
    return Math.min(100, Math.round(percent));
  };

  useEffect(() => {
    if (!selectedAnalysis) return undefined;

    const closeOnEscape = (event) => {
      if (event.key === "Escape") setSelectedAnalysis(null);
    };

    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [selectedAnalysis]);

  useEffect(() => {
    let mounted = true;

    const refreshModelStatus = async () => {
      try {
        const response = await aiAPI.getModelStatus();
        if (mounted) setActiveAiModels(response.data.activeModels || []);
      } catch {
        // Status polling must not interrupt normal AI Assistant use.
      }
    };

    refreshModelStatus();
    const intervalId = window.setInterval(refreshModelStatus, 3000);
    return () => {
      mounted = false;
      window.clearInterval(intervalId);
    };
  }, []);

  const handleDownload = async (fields, type) => {
    if (!fields) return;
    setLoading(true);
    try {
      const candidateName = (fields.name || "Resume").trim().replace(/\s+/g, "_");

      const res = await aiAPI.downloadFormattedResume({
        ...fields,
        format: type,
        originalFileName: `UTS_${candidateName}`,
      });

      const blob = new Blob([res.data], {
        type:
          type === "pdf"
            ? "application/pdf"
            : "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `UTS_${candidateName}.${type}`;
      link.click();
      window.URL.revokeObjectURL(url);

      toast.success("Resume downloaded");
    } catch {
      toast.error("Download failed");
    } finally {
      setLoading(false);
    }
  };

  /* ================= UI ================= */

  return (
    <div className="ai-container">
      <div className="ai-header">
        <h2>AI Assistant</h2>
        <p className="subtitle">AI-powered resume matching & job intelligence</p>
      </div>

      <div className="tabs">
        <button
          className={activeTab === "analyze" ? "tab active" : "tab"}
          onClick={() => setActiveTab("analyze")}
        >
          Resume vs JD
        </button>
        <button
          className={activeTab === "suggestions" ? "tab active" : "tab"}
          onClick={() => setActiveTab("suggestions")}
        >
          JD Suggestions
        </button>
        <button
          className={activeTab === "format" ? "tab active" : "tab"}
          onClick={() => setActiveTab("format")}
        >
          Format Resume
        </button>
      </div>

      {/* ================= Resume vs JD ================= */}
      {activeTab === "analyze" && (
        <div className="tab-panel">
          <div className="upload-controls">
            <label className="resume-count-selector">
              <span className="ai-model-selector">
                <span>AI Model</span>
                <select
                  value={selectedAiModel}
                  onChange={(e) => setSelectedAiModel(e.target.value)}
                  disabled={parsing || loading}
                  aria-label="AI model for resume analysis"
                >
                  <option
                    value="gemini-2.5-flash"
                    disabled={activeAiModels.includes("gemini-2.5-flash")}
                  >
                    Gemini 2.5 Flash (Primary)
                  </option>
                  <option
                    value="gemini-2.5-flash-lite"
                    disabled={activeAiModels.includes("gemini-2.5-flash-lite")}
                  >
                    Gemini 2.5 Flash Lite
                  </option>
                </select>
              </span>
              <span>No.of Resumes</span>
              <select
                value={resumeUploadLimit}
                onChange={handleResumeUploadLimitChange}
                disabled={parsing}
                aria-label="Number of resumes to upload"
              >
                {[1, 5, 10, 15, 20, 25].map((count) => (
                  <option key={count} value={count}>{count}</option>
                ))}
              </select>
            </label>
          </div>
          <div className="upload-box">
            <input
              ref={resumeInputRef}
              type="file"
              hidden
              accept=".txt,.pdf,.doc,.docx"
              multiple
              onChange={handleResumeFile}
            />
            <LoadingButton
              className="upload-btn"
              onClick={() => resumeInputRef.current.click()}
              disabled={remainingResumeSlots === 0}
            >
              {remainingResumeSlots > 0
                ? `Upload resume (${remainingResumeSlots} remaining)`
                : `Upload limit reached (${resumeUploadLimit})`}
            </LoadingButton>
            {resumeUploads.length > 0 && (
              <div className="resume-upload-statuses" aria-live="polite">
                {resumeUploads.map((upload) => (
                  <div key={upload.id} className={`resume-upload-status ${upload.status}`}>
                    <span className="resume-upload-name">{upload.fileName}</span>
                    <span className="resume-upload-state">
                      {upload.status === "parsing" ? "Parsing..." : upload.status === "complete" ? "Complete" : "Failed"}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="input-group">
            <label>Job Description</label>
            <textarea
              placeholder="Paste job description here..."
              value={jobDescription}
              onChange={(e) => setJobDescription(e.target.value)}
              rows={6}
            />
          </div>

          <div className="input-group primary-skills-group">
            <div className="primary-skills-header">
              <label>Primary Skills</label>
              <button
                type="button"
                className="add-skills-btn"
                onClick={() => setShowPrimarySkillInput(true)}
                disabled={showPrimarySkillInput}
              >
                Add Skills
              </button>
            </div>

            {showPrimarySkillInput && (
              <div className="primary-skill-entry">
                <input
                  type="text"
                  placeholder="Enter skill (separate multiple skills with commas)"
                  value={newPrimarySkill}
                  onChange={(e) => setNewPrimarySkill(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleSavePrimarySkills();
                    }
                  }}
                  autoFocus
                />
                <button type="button" className="save-skills-btn" onClick={handleSavePrimarySkills}>
                  Save
                </button>
              </div>
            )}

            {primarySkills.length > 0 && (
              <div className="tags primary-skill-tags">
                {primarySkills.map((skill) => (
                  <span key={skill} className="tag tag-purple primary-skill-tag">
                    {skill}
                    <button
                      type="button"
                      className="remove-primary-skill"
                      onClick={() => handleRemovePrimarySkill(skill)}
                      aria-label={`Remove ${skill}`}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <LoadingButton className="btn-primary" onClick={handleAnalyzeResume} loading={loading} disabled={parsing || activeAiModels.includes(selectedAiModel)} loadingText="Analyzing...">
            Analyze Resume
          </LoadingButton>

          {analysisResults.length > 0 && (
            <div className="results">
              <h3>Resume Match Results</h3>
              <div className="resume-results-table-wrap">
                <table className="resume-results-table">
                  <thead>
                    <tr>
                      <th scope="col">S.No</th>
                      <th scope="col">Name</th>
                      <th scope="col">Match Score</th>
                      <th scope="col">Years of Experience</th>
                      <th scope="col">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analysisResults.map((result, index) => (
                      <tr key={`${result.fileName}-${index}`}>
                        <td>{index + 1}</td>
                        <td>{result.name || result.fileName || "Not available"}</td>
                        <td>{getMatchPercent(result.analysis?.matchScore)}%</td>
                        <td>{result.experience || "Not available"}</td>
                        <td>
                          <button
                            type="button"
                            className="view-analysis-btn"
                            onClick={() => setSelectedAnalysis(result)}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {selectedAnalysis && (
                <div
                  className="analysis-modal-overlay"
                  role="presentation"
                  onMouseDown={() => setSelectedAnalysis(null)}
                >
                  <div
                    className="analysis-modal"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="analysis-details-title"
                    onMouseDown={(event) => event.stopPropagation()}
                  >
                    <div className="analysis-modal-header">
                      <div>
                        <h3 id="analysis-details-title">Analysis Details</h3>
                        <div className="analysis-candidate-summary">
                          <p className="analysis-candidate-name">
                            {selectedAnalysis.name || selectedAnalysis.fileName || "Candidate"}
                          </p>
                          {selectedAnalysis.experience && (
                            <span className="analysis-candidate-experience">
                              {selectedAnalysis.experience}
                            </span>
                          )}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="analysis-modal-close"
                        onClick={() => setSelectedAnalysis(null)}
                        aria-label="Close analysis details"
                      >
                        ×
                      </button>
                    </div>
                    <div className="analysis-modal-body">
                  <div className="score-display">
                    <span className="score-number">{getMatchPercent(selectedAnalysis.analysis?.matchScore)}%</span>
                    <span className="score-label">Match Score</span>
                  </div>

                  <div className="section">
                    <h4>Strengths</h4>
                    <div className="tags">
                      {selectedAnalysis.analysis?.strengths?.map((item, idx) => (
                        <span key={idx} className="tag tag-green">{item}</span>
                      ))}
                    </div>
                  </div>

                  <div className="section">
                    <h4>Missing Skills</h4>
                    <div className="tags">
                      {selectedAnalysis.analysis?.missingSkills?.map((item, idx) => (
                        <span key={idx} className="tag tag-yellow">{item}</span>
                      ))}
                    </div>
                  </div>

                  <div className="section">
                    <h4>AI Recommendation</h4>
                    <div className="recommendation-box">
                      {selectedAnalysis.analysis?.recommendation}
                    </div>
                  </div>

                  <div className="analysis-modal-actions">
                    <button
                      type="button"
                      className="analysis-modal-close-button"
                      onClick={() => setSelectedAnalysis(null)}
                    >
                      Close
                    </button>
                  </div>
                </div>
                  </div>
                </div>
              )}
            </div>
          )}

        </div>
      )}

      {/* ================= JD Suggestions ================= */}
      {activeTab === "suggestions" && (
        <div className="tab-panel">
          <div className="input-group">
            <label>📋 Job Description</label>
            <textarea
              placeholder="Paste job description here..."
              value={jobDescription}
              onChange={(e) => setJobDescription(e.target.value)}
              rows={6}
            />
          </div>

          <LoadingButton className="btn-primary" onClick={handleGetSuggestions} loading={loading} loadingText="Generating...">
            Generate Suggestions
          </LoadingButton>

          {suggestions && (
            <div className="results">
              <h3>JD Search Helper</h3>

              <div className="action-btns">
                <button
                  className="btn-outline"
                  onClick={() =>
                    navigator.clipboard.writeText(
                      JSON.stringify(suggestions, null, 2)
                    )
                  }
                >
                  Copy All Keywords
                </button>
                <button
                  className="btn-success"
                  onClick={() =>
                    navigator.clipboard.writeText(suggestions.booleanSearch)
                  }
                >
                  Copy Boolean Search
                </button>
              </div>

              <div className="section">
                <h4>Job Titles</h4>
                <div className="tags">
                  {suggestions.jobTitles?.map((item, idx) => (
                    <span key={idx} className="tag tag-blue">
                      {item}
                    </span>
                  ))}
                </div>
              </div>

              <div className="section">
                <h4>Primary Skills</h4>
                <div className="tags">
                  {suggestions.primarySkills?.map((item, idx) => (
                    <span key={idx} className="tag tag-purple">
                      {item}
                    </span>
                  ))}
                </div>
              </div>

              <div className="section">
                <h4>Secondary Skills</h4>
                <div className="tags">
                  {suggestions.secondarySkills?.map((item, idx) => (
                    <span key={idx} className="tag tag-gray">
                      {item}
                    </span>
                  ))}
                </div>
              </div>

              <div className="section">
                <h4>Tools & Technologies</h4>
                <div className="tags">
                  {suggestions.toolsAndTechnologies?.map((item, idx) => (
                    <span key={idx} className="tag tag-gray">
                      {item}
                    </span>
                  ))}
                </div>
              </div>

              <div className="section">
                <h4>Boolean Search</h4>
                <div className="boolean-box">{suggestions.booleanSearch}</div>
              </div>
            </div>
          )}
        </div>
      )}
      

      {/* ================= Format Resume ================= */}
      {activeTab === "format" && (
        <div className="tab-panel">
          <div className="upload-controls">
            <label className="resume-count-selector">
              <span className="ai-model-selector">
                <span>AI Model</span>
                <select
                  value={formatAiModel}
                  onChange={(e) => setFormatAiModel(e.target.value)}
                  disabled={loading}
                  aria-label="AI model for resume formatting"
                >
                  <option value="gemini-2.5-flash">Gemini 2.5 Flash (Primary)</option>
                  <option value="gemini-2.5-flash-lite">Gemini 2.5 Flash Lite</option>
                </select>
              </span>
              <span>No.of Resumes</span>
              <select
                value={formatResumeUploadLimit}
                onChange={handleFormatUploadLimitChange}
                disabled={loading}
                aria-label="Number of resumes to format"
              >
                {[1, 5, 10, 15, 20, 25].map((count) => (
                  <option key={count} value={count}>{count}</option>
                ))}
              </select>
            </label>
          </div>
          <div className="upload-box">
            <input
              ref={formatInputRef}
              type="file"
              hidden
              accept=".txt,.pdf,.doc,.docx"
              multiple
              onChange={handleFormatFile}
            />
            <LoadingButton
              className="upload-btn"
              onClick={() => formatInputRef.current?.click()}
              disabled={loading || formatUploads.length === formatResumeUploadLimit}
            >
              {formatUploads.length < formatResumeUploadLimit
                ? `Upload resume (${formatResumeUploadLimit - formatUploads.length} remaining)`
                : `Upload limit reached (${formatResumeUploadLimit})`}
            </LoadingButton>
            {formatUploads.length > 0 && (
              <div className="resume-upload-statuses" aria-live="polite">
                {formatUploads.map((upload) => (
                  <div
                    key={upload.id}
                    className={`resume-upload-status ${upload.status}`}
                  >
                    <span className="resume-upload-name">{upload.fileName}</span>
                    <span className="resume-upload-state">
                      {upload.status === "ready" ? "Ready" : upload.status === "extracting" ? "Extracting..." : upload.status === "complete" ? "Complete" : "Failed"}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {formatUploads.some((upload) => upload.status === "ready") && (
            <LoadingButton className="btn-primary" onClick={handleConvertResume} loading={loading} loadingText="Extracting...">
              Extract Resume Data
            </LoadingButton>
          )}

          {formatUploads.some((upload) => upload.status === "complete" || upload.status === "failed") && (
            <div className="results">
              <h3>Formatted Resume Results</h3>
              <div className="resume-results-table-wrap">
                <table className="resume-results-table">
                  <thead>
                    <tr>
                      <th scope="col">S.No</th>
                      <th scope="col">Resume Name</th>
                      <th scope="col">Download Format</th>
                      <th scope="col">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {formatUploads.map((upload, index) => (
                      <tr key={upload.id}>
                        <td>{index + 1}</td>
                        <td>{upload.fields?.name || upload.fileName}</td>
                        <td>
                          {upload.fields ? (
                            <select
                              value={upload.downloadType || "pdf"}
                              onChange={(event) => handleDownloadTypeChange(upload.id, event.target.value)}
                              aria-label={`Download format for ${upload.fileName}`}
                            >
                              <option value="pdf">PDF</option>
                              <option value="docx">DOCX</option>
                            </select>
                          ) : "—"}
                        </td>
                        <td>
                          {upload.fields ? (
                            <div className="format-result-actions">
                              <button type="button" className="view-analysis-btn" onClick={() => handleOpenFormatModal(upload, "view")}>View</button>
                              <button type="button" className="view-analysis-btn" onClick={() => handleOpenFormatModal(upload, "edit")}>Edit</button>
                              <LoadingButton className="view-analysis-btn" onClick={() => handleDownload(upload.fields, upload.downloadType || "pdf")} loading={loading} loadingText="Downloading...">Download</LoadingButton>
                            </div>
                          ) : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {false && resumeFields && (
            <div className="results">
              <h3>Edit Resume Fields{formatUploads.length > 1 ? ` — ${formatUploads.find((upload) => upload.id === selectedFormatUploadId)?.fileName || "Selected resume"}` : ""}</h3>

              {(() => {
                const FIELD_ORDER = [
                  'name', 'role', 'total_experience',
                  'experience_summary', 'technical_skills', 'work_experience',
                  'key_projects_summary', 'projects', 'education', 'additional_information'
                ];
                const multilineKeys = [
                  'experience_summary', 'technical_skills', 'work_experience',
                  'key_projects_summary', 'projects', 'education', 'additional_information'
                ];
                const orderedEntries = [
                  ...FIELD_ORDER.filter(k => k in resumeFields).map(k => [k, resumeFields[k]]),
                  ...Object.entries(resumeFields).filter(([k]) => !FIELD_ORDER.includes(k)),
                ];
                return orderedEntries.map(([key, value]) => {
                  const rowCount = multilineKeys.includes(key) ? 8 : 2;
                  return (
                  <div key={key} className="input-group">
                    <label>
                      {key
                        .replace(/_/g, " ")
                        .replace(/\b\w/g, (l) => l.toUpperCase())}
                    </label>
                    <textarea
                      value={value || ""}
                      onChange={(e) => handleFieldChange(key, e.target.value)}
                      rows={rowCount}
                      style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}
                    />
                  </div>
                  );
                });
              })()}

              <div className="input-group">
                <label>Download Format</label>
                <select
                  value={downloadType}
                  onChange={(e) => setDownloadType(e.target.value)}
                >
                  <option value="pdf">PDF</option>
                  <option value="docx">DOCX</option>
                </select>
              </div>

              <LoadingButton className="btn-success" onClick={handleDownload} loading={loading} loadingText="Preparing...">
                Download Resume
              </LoadingButton>
            </div>
          )}

          {formatModal && (() => {
            const selectedUpload = formatUploads.find((upload) => upload.id === formatModal.uploadId);
            const fields = formatModal.mode === "edit" ? formatEditFields : selectedUpload?.fields;
            if (!selectedUpload || !fields) return null;
            const isEdit = formatModal.mode === "edit";
            return (
              <div className="analysis-modal-overlay" role="presentation" onMouseDown={() => setFormatModal(null)}>
                <div className="analysis-modal format-resume-modal" role="dialog" aria-modal="true" aria-labelledby="format-resume-title" onMouseDown={(event) => event.stopPropagation()}>
                  <div className="analysis-modal-header"><div><h3 id="format-resume-title">{isEdit ? "Edit Resume" : "Resume Details"}</h3><p className="analysis-candidate-name">{selectedUpload.fileName}</p></div></div>
                  <div className="analysis-modal-body">
                    {FORMAT_FIELD_ORDER.map((key) => (
                      <div key={key} className="input-group format-modal-field">
                        <label>{key.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase())}</label>
                        {isEdit ? <textarea value={fields[key] || ""} onChange={(event) => handleFieldChange(key, event.target.value)} rows={FORMAT_MULTILINE_FIELDS.has(key) ? 7 : 2} /> : <div className="format-field-view">{fields[key] || "Not available"}</div>}
                      </div>
                    ))}
                    <div className="format-modal-actions">
                      {isEdit && <button type="button" className="btn-success" onClick={handleSaveFormatFields}>Save</button>}
                      <button type="button" className="btn-secondary" onClick={() => setFormatModal(null)}>Close</button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}
